import React from 'react';
import { hasFeature } from '../../state/zustand';
import {
  useRefreshShipnetOrdersMutation,
  useSetupShipnetCoaMutation,
  useSyncBookingListMutation,
  useSyncShipnetOrdersMutation,
  useUpdateDateShipnetMutation,
  useUpdateShipnetScenarioMutation,
} from '../../graphql';
import useEvent from '../hooks/useEvent';
import { DropdownButton, Menu, MenuItem } from '../ui';
import {
  createLoadingToast,
  handleApolloError,
  updateToastError,
  updateToastSuccess,
} from './helpers';
import { useSharedNotificationCenter } from '../contexts/NotificationsContext';
import { v1 as uuid } from 'uuid';

export function ShipnetUpdates({
  restoreIntegrationScenario,
}: {
  restoreIntegrationScenario: () => void;
}): JSX.Element | null {
  const isShipnetToolsFeatureAvailable = hasFeature('shipnet_tools');
  const isShipnetToolsOneWayAvailable = hasFeature('shipnet_tools_oneway');
  const [shipnetUpdateScenarioMutation] = useUpdateShipnetScenarioMutation();
  const [syncShipnetOrdersMutation] = useSyncShipnetOrdersMutation();
  const [refreshShipnetOrdersMutation] = useRefreshShipnetOrdersMutation();
  const [updateShipnetDateMutation] = useUpdateDateShipnetMutation();
  const [syncBookingListMutation] = useSyncBookingListMutation();
  const [setupShipnetCoasMutation] = useSetupShipnetCoaMutation();
  const { add } = useSharedNotificationCenter();

  const updateShipnetScenario = useEvent(async () => {
    const toastId = createLoadingToast('Updating ShipNet scenario');
    try {
      const result = await shipnetUpdateScenarioMutation();

      if (result.errors && result.errors?.length > 0) {
        throw result.errors;
      }

      const warnings = result.data?.updateShipnetScenario?.warnings || [];
      if (warnings.length > 0) {
        warnings.forEach((warning: string) =>
          add({ id: uuid(), content: warning, type: 'warning' }),
        );
        updateToastSuccess(toastId, 'ShipNet scenario updated with warnings');
      } else {
        updateToastSuccess(
          toastId,
          `ShipNet scenario updated with ${result.data?.updateShipnetScenario?.generatedTCLength} assignments`,
        );
      }

      restoreIntegrationScenario();
    } catch (error) {
      updateToastError(toastId, 'Shipnet scenario failed to update');
      handleApolloError(error);
    }
  });

  const setupShipnetOrders = useEvent(async () => {
    const toastId = createLoadingToast('Syncing orders');
    try {
      const result = await syncShipnetOrdersMutation();

      if (result.errors && result.errors?.length > 0) {
        throw result.errors;
      }

      updateToastSuccess(toastId, 'Sync orders process completed');
    } catch (error) {
      updateToastError(toastId, 'Failed to trigger ShipNet orders creation');
      handleApolloError(error);
    }
  });

  const refreshShipnetSchedule = useEvent(async () => {
    const toastId = createLoadingToast('Triggering refresh ShipNet schedule');
    try {
      const result = await refreshShipnetOrdersMutation();

      if (result.errors && result.errors?.length > 0) {
        throw result.errors;
      }

      updateToastSuccess(
        toastId,
        'Triggered refresh ShipNet schedule successfully',
      );
    } catch (error) {
      updateToastError(toastId, 'Failed to trigger refresh ShipNet schedule');
      handleApolloError(error);
    }
  });

  const updateShipnetDate = useEvent(async () => {
    const toastId = createLoadingToast('Queuing updates date');
    try {
      const result = await updateShipnetDateMutation();

      if (result.errors && result.errors?.length > 0) {
        throw result.errors;
      }

      updateToastSuccess(toastId, 'Queued updates date succesfully');
    } catch (error) {
      updateToastError(toastId, 'Failed to trigger ShipNet updates date');
      handleApolloError(error);
    }
  });

  const setupShipnetCoas = useEvent(async () => {
    const toastId = createLoadingToast('Queuing COAs creation ...');
    try {
      const result = await setupShipnetCoasMutation();

      if (result.errors && result.errors?.length > 0) {
        throw result.errors;
      }

      updateToastSuccess(toastId, 'Queued COAs creation successfully');
    } catch (error) {
      updateToastError(toastId, 'Failed to trigger ShipNet COAs creation');
      handleApolloError(error);
    }
  });

  const setupBookingList = useEvent(async () => {
    const toastId = createLoadingToast('Start to update from booking list ');
    try {
      const result = await syncBookingListMutation();

      if (result.errors && result.errors?.length > 0) {
        throw result.errors;
      }

      updateToastSuccess(toastId, 'Get updates from booking list successfully');
    } catch (error) {
      updateToastError(toastId, 'Failed to get updates from booking list');
      handleApolloError(error);
    }
  });

  const shipnetUpdateData = useEvent(async () => {
    try {
      await setupBookingList();
      await setupShipnetOrders();
      await updateShipnetScenario();
    } catch (error) {
      console.error('Error during Refresh Shipnet Data:', error);
    }
  });

  if (!isShipnetToolsFeatureAvailable && !isShipnetToolsOneWayAvailable)
    return null;
  return (
    <DropdownButton
      data-testid="shipnet-tools"
      variant="dark"
      label="ShipNet tools"
      mr={3}
    >
      <Menu hasShadow>
        {isShipnetToolsOneWayAvailable && (
          <MenuItem label="Refresh Shipnet Data" onPress={shipnetUpdateData} />
        )}
        <MenuItem label="Update scenario" onPress={updateShipnetScenario} />
        {isShipnetToolsOneWayAvailable && (
          <MenuItem label={'Update orders'} onPress={setupShipnetOrders} />
        )}
        {isShipnetToolsOneWayAvailable && (
          <MenuItem
            label={'Update from booking list'}
            onPress={setupBookingList}
          />
        )}
        {!isShipnetToolsOneWayAvailable && (
          <MenuItem label="Refresh schedule" onPress={refreshShipnetSchedule} />
        )}
        {!isShipnetToolsOneWayAvailable && (
          <MenuItem label="Update dates" onPress={updateShipnetDate} />
        )}
        <MenuItem label="Update COAs" onPress={setupShipnetCoas} />
      </Menu>
    </DropdownButton>
  );
}
